import { Page } from "./base";
import profilerTemplate from "../templates/pages/profiler.html?raw";
import NavBar from "../sections/navbar";
import Footer from "../sections/footer";
import { TastePreferences, UserProfile, WineProfile } from "../types/user";
import { Icons } from "../services/icons";
import { getElementById } from "../services/dom";
import { submitUserProfile } from "../services/profile";
import pastaImage from "../assets/wizard/pasta.png";
import flavours from "../assets/wizard/flavours.png";
import ingredientsImg from "../assets/wizard/ingredients.png";
import recipeBook from "../assets/wizard/recipebook.png";
import budget from "../assets/wizard/budget.png";
import wineExperience from "../assets/wizard/wineExperience.png";
import party from "../assets/wizard/party.png";
import wineColours from "../assets/wizard/wineColours.png";
import globe from "../assets/wizard/globe.png";
import wineGrapes from "../assets/wizard/wineGrapes.png";

const STEPS = [
  { id: 1, title: "Favorite Cuisines", description: "Which culinary traditions do you enjoy?", experienceLevel: "all" },
  { id: 2, title: "Flavor Profiles", description: "What flavor characteristics appeal to you?", experienceLevel: "all" },
  { id: 3, title: "Key Ingredients", description: "Which ingredients feature in your favorite dishes?", experienceLevel: "all" },
  { id: 4, title: "Recipe Types", description: "What types of dishes do you cook or order?", experienceLevel: "all" },
  { id: 5, title: "Budget Range", description: "What's your typical spending range for wine?", experienceLevel: "all" },
  { id: 6, title: "Wine Experience", description: "How would you describe your wine knowledge?", experienceLevel: "all" },
  { id: 7, title: "Wine Occasions", description: "When do you typically enjoy wine?", experienceLevel: "all" },
  { id: 8, title: "Wine Types", description: "Rate your preference for different wine styles", experienceLevel: "advanced" },
  { id: 9, title: "Wine Regions", description: "Select your favorite wine regions", experienceLevel: "advanced" },
  { id: 10, title: "Wine Taste Preferences", description: "Help us understand your wine taste preferences", experienceLevel: "advanced" },
  { id: 11, title: "Flavor Profiles", description: "Tell us about flavors you love and dislike", experienceLevel: "advanced" },
  { id: 12, title: "Drinking Habits", description: "Share your wine consumption patterns", experienceLevel: "advanced" },
  { id: 13, title: "Final Details", description: "Complete your wine profile", experienceLevel: "advanced" },
];

export default class ProfilerPage extends Page {
  private currentStep: number = 0; // Start at 0 for welcome page
  private availableSteps: any[] = [];
  private isProcessing: boolean = false;
  private showProfileType: boolean = false;
  private showWelcome: boolean = true;
  private eventListenersAttached: boolean = false;
  private submissionError: string | null = null;
  private isSubmittingToAPI: boolean = false;
  private determinedProfileType: any = null;
  private userProfile: UserProfile = {
    taste: {
      cuisines: [],
      flavors: [],
      ingredients: [],
      recipes: [],
      sweetness_preferred_level: 5,
      sweetness_tolerance_min: 1,
      sweetness_tolerance_max: 10,
      acidity_preferred_level: 5,
      acidity_tolerance_min: 1,
      acidity_tolerance_max: 10,
      tannin_preferred_level: 5,
      body_preference: "no_preference",
      alcohol_preferred_min: 10.0,
      alcohol_preferred_max: 14.0,
      alcohol_sensitivity: "medium",
    },
    wine: {
      budget: "",
      experience: "",
      occasions: [],
      wine_types: [],
      preferred_regions: [],
      loved_flavors: [],
      disliked_flavors: [],
      drinking_habits: {
        frequency: "",
        everyday_wine_min_budget: 0,
        everyday_wine_max_budget: 0,
        special_occasion_min_budget: 0,
        special_occasion_max_budget: 0,
        budget_currency: "USD"
      },
      dietary_restrictions: [],
      pairing_styles: [],
      openness_to_new_wines: 5,
    },
  };
  constructor() {
    super(profilerTemplate);
    this.updateAvailableSteps();
    this.loadPage();
  }

  public async loadPage(): Promise<void> {
    super.init();
    new NavBar();
    new Footer();
    this.render();
  }

  private updateTastePreferences(taste: TastePreferences): void {
    this.userProfile.taste = taste;
    this.render();
  }

  private updateWineProfile(wine: WineProfile): void {
    this.userProfile.wine = wine;
    this.updateAvailableSteps();
    this.render();
  }

  private updateAvailableSteps(): void {
    const experienceLevel = this.userProfile.wine.experience;
    const isAdvanced = experienceLevel === 'advanced' || experienceLevel === 'expert';

    if (isAdvanced) {
      this.availableSteps = STEPS;
    } else {
      this.availableSteps = STEPS.filter(step => step.experienceLevel === 'all');
    }

    // If we're currently on an advanced step that's no longer available, start processing
    if (!isAdvanced && this.currentStep > 7 && this.currentStep < 14) {
      this.isProcessing = true;
    }
  }

  private nextStep(): void {
    if (this.showWelcome) {
      // Move from welcome page to first step
      this.showWelcome = false;
      this.currentStep = 1;
      this.renderWithTransition();
    } else {
      const currentIndex = this.availableSteps.findIndex(step => step.id === this.currentStep);
      if (currentIndex < this.availableSteps.length - 1) {
        this.currentStep = this.availableSteps[currentIndex + 1].id;
        this.renderWithTransition();
      } else {
        // Reached the end of steps, start processing
        this.isProcessing = true;
        this.renderWithTransition();
      }
    }
  }

  private prevStep(): void {
    const currentIndex = this.availableSteps.findIndex(step => step.id === this.currentStep);
    if (currentIndex > 0) {
      this.currentStep = this.availableSteps[currentIndex - 1].id;
      this.renderWithTransition();
    }
  }

  private isStepComplete(step: number): boolean {
    switch (step) {
      case 1: // Favorite Cuisines
        return this.userProfile.taste.cuisines.length > 0;
      case 2: // Flavor Profiles
        return this.userProfile.taste.flavors.length > 0;
      case 3: // Key Ingredients
        return this.userProfile.taste.ingredients.length > 0;
      case 4: // Recipe Types
        return this.userProfile.taste.recipes.length > 0;
      case 5: // Budget Range
        return this.userProfile.wine.budget !== "";
      case 6: // Wine Experience
        return this.userProfile.wine.experience !== "";
      case 7: // Wine Occasions
        return this.userProfile.wine.occasions.length > 0;
      case 8: // Wine Types (advanced)
        return this.userProfile.wine.wine_types! && this.userProfile.wine.wine_types.length > 0;
      case 9: // Wine Regions (advanced)
        return this.userProfile.wine.preferred_regions! && this.userProfile.wine.preferred_regions.length > 0;
      case 10: // Wine Taste Preferences (advanced)
        return true; // Always complete as it has default slider values
      case 11: // Flavor Profiles (advanced)
        return (this.userProfile.wine.loved_flavors! && this.userProfile.wine.loved_flavors.length > 0) || 
               (this.userProfile.wine.disliked_flavors! && this.userProfile.wine.disliked_flavors.length > 0);
      case 12: // Drinking Habits (advanced)
        return this.userProfile.wine.drinking_habits?.frequency !== "";
      case 13: // Final Details (advanced)
        return !!(this.userProfile.wine.dietary_restrictions && this.userProfile.wine.dietary_restrictions.length > 0) && 
               !!(this.userProfile.wine.pairing_styles && this.userProfile.wine.pairing_styles.length > 0);
      default:
        return false;
    }
  }

  private createWelcomeStep(): string {
    return `
      <div class="animate-fade-in">
        <div class="max-w-2xl mx-auto text-center">
          <div class="mb-8">
            <h1 class="text-4xl font-bold text-wine-dark mb-6">
              Welcome to Your Wine Journey
            </h1>
            <p class="text-lg text-muted-foreground leading-relaxed">
              In order to set your profile up, we'll need to ask a few questions. It helps us understand your wine habits, to tailor your recommendations.
            </p>
          </div>
          
          <div class="mt-12">
            <button
              id="get-started-btn"
              class="btn-primary text-primary-foreground px-8 py-4 text-lg rounded-md transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              Get Started
              ${Icons.chevronRight("h-5 w-5")}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private createProgress(): string {
    const currentStepIndex = this.availableSteps.findIndex(step => step.id === this.currentStep);
    const totalSteps = this.availableSteps.length;

    // Dynamic sizing based on number of steps
    const isExtended = totalSteps > 7;
    const dotSize = isExtended ? 'w-1.5 h-1.5 xs:w-2 xs:h-2 sm:w-3 sm:h-3 md:w-4 md:h-4' : 'w-2 h-2 xs:w-3 xs:h-3 sm:w-4 sm:h-4 md:w-6 md:h-6';
    const lineSize = isExtended ? 'w-1 xs:w-1.5 sm:w-2 md:w-4' : 'w-2 xs:w-3 sm:w-4 md:w-8 lg:w-12';
    const marginSize = isExtended ? 'mx-0.5 xs:mx-0.5 sm:mx-1' : 'mx-0.5 xs:mx-1 md:mx-2';

    const dotsHtml = this.availableSteps.map(
      (_, index) => {
        const isReached = currentStepIndex >= index;
        const isLast = index === totalSteps - 1;

        return `
          <div class="flex items-center">
            <div class="relative">
              <div class="${dotSize} rounded-full border-2 transition-all duration-300 ${isReached ? 'border-burgundy bg-burgundy' : 'border-gray-300 bg-white'}"></div>
            </div>
            ${!isLast ? `<div class="${lineSize} h-0.5 md:h-1 bg-gray-300 ${marginSize}"></div>` : ''}
          </div>
        `;
      }
    ).join("");

    return `
          <div class="max-w-full px-1 xs:px-2 sm:px-4 mx-auto mb-8">
            <div class="flex items-center justify-center mb-4">
              ${dotsHtml}
            </div>
          </div>
        `;
  }

  private createNavigation(): string {
    return `
          <div class="mx-auto mt-8 flex justify-between items-center text-lg">
            <button
              id="prev-btn"
              class="flex items-center justify-center gap-2 px-4 py-2 border border-black border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors ${this.currentStep === this.availableSteps[0].id ? "opacity-50 cursor-not-allowed" : ""}"
              ${this.currentStep === this.availableSteps[0].id ? "disabled" : ""}
            >
              ${Icons.chevronLeft("h-4 w-4")}
              Previous
            </button>

            <div class="text-sm text-muted-foreground">
              Step ${this.availableSteps.findIndex(s => s.id === this.currentStep) + 1} of ${this.availableSteps.length}
            </div>

            <button
              id="next-btn"
              class="flex items-center justify-center gap-2 px-4 py-2 border border-black btn-primary text-primary-foreground rounded-md transition-all duration-300 ${!this.isStepComplete(this.currentStep) ? "opacity-50 cursor-not-allowed" : ""}"
              ${!this.isStepComplete(this.currentStep) ? "disabled" : ""}
            >
              ${this.currentStep === this.availableSteps[this.availableSteps.length - 1].id ? "Complete" : "Next"}
              ${Icons.chevronRight("h-4 w-4")}
            </button>
          </div>
        `;
  }

  private createStepContent(): string {
    switch (this.currentStep) {
      case 1:
        return this.createCuisinesStep();
      case 2:
        return this.createFlavorProfilesStep();
      case 3:
        return this.createIngredientsStep();
      case 4:
        return this.createRecipeTypesStep();
      case 5:
        return this.createBudgetRangeStep();
      case 6:
        return this.createWineExperienceStep();
      case 7:
        return this.createWineOccasionsStep();
      case 8:
        return this.createWineTypesStep();
      case 9:
        return this.createWineRegionsStep();
      case 10:
        return this.createWineTastePreferencesStep();
      case 11:
        return this.createAdvancedFlavorProfilesStep();
      case 12:
        return this.createDrinkingHabitsStep();
      case 13:
        return this.createFinalDetailsStep();
      default:
        return "";
    }
  }

  private createCuisinesStep(): string {
    const cuisines = ["Italian", "French", "Mediterranean", "Asian", "Mexican", "Indian", "Middle Eastern", "American", "Spanish", "Japanese", "Thai", "Greek", "Malaysian", "Vietnamese", "Cambodian"];

    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                Tell us about your favorite cuisines
              </h2>
              <p class="text-muted-foreground">
                Select the culinary traditions you enjoy most. This helps us understand your palate.
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${pastaImage}" alt="Pasta dish" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6 card-hover transition-all duration-300">
                <div class="flex flex-wrap justify-center gap-2">
                  ${cuisines.map((item) => {
                    const isSelected = (this.userProfile.taste.cuisines as string[]).includes(item);
                    return `
                        <button
                          data-category="cuisines"
                          data-item="${item}"
                          class="taste-selection-btn px-3 py-1 text-lg rounded-md transition-all duration-200 ${isSelected ? "btn-primary text-primary-foreground bg-burgundy text-white shadow-wine" : "border border-input bg-background hover:border-wine-burgundy hover:text-wine-burgundy"}"
                        >
                          ${item}
                        </button>
                      `;
                  }).join("")}
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createFlavorProfilesStep(): string {
    const flavors = ["Spicy", "Sweet", "Savory", "Smoky", "Citrusy", "Herbal", "Rich & Creamy", "Light & Fresh", "Bold & Intense", "Mild & Subtle"];

    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                What flavor profiles appeal to you?
              </h2>
              <p class="text-muted-foreground">
                Select the flavor characteristics that you enjoy most in your food.
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${flavours}" alt="Flavours" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6 card-hover transition-all duration-300">
                <div class="flex flex-wrap justify-center gap-2">
                  ${flavors.map((item) => {
                    const isSelected = (this.userProfile.taste.flavors as string[]).includes(item);
                    return `
                        <button
                          data-category="flavors"
                          data-item="${item}"
                          class="taste-selection-btn px-3 py-1 text-lg rounded-md transition-all duration-200 ${isSelected ? "btn-primary text-primary-foreground bg-burgundy text-white shadow-wine" : "border border-input bg-background hover:border-wine-burgundy hover:text-wine-burgundy"}"
                        >
                          ${item}
                        </button>
                      `;
                  }).join("")}
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createIngredientsStep(): string {
    const ingredients = ["Garlic", "Herbs", "Cheese", "Tomatoes", "Mushrooms", "Seafood", "Red Meat", "Poultry", "Vegetables", "Fruits", "Nuts", "Chocolate"];

    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                What are your key ingredients?
              </h2>
              <p class="text-muted-foreground">
                Select the ingredients that feature most in your favorite dishes.
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${ingredientsImg}" alt="Ingredients" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6 card-hover transition-all duration-300">
                <div class="flex flex-wrap justify-center gap-2">
                  ${ingredients.map((item) => {
                    const isSelected = (this.userProfile.taste.ingredients as string[]).includes(item);
                    return `
                        <button
                          data-category="ingredients"
                          data-item="${item}"
                          class="taste-selection-btn px-3 py-1 text-lg rounded-md transition-all duration-200 ${isSelected ? "btn-primary text-primary-foreground bg-burgundy text-white shadow-wine" : "border border-input bg-background hover:border-wine-burgundy hover:text-wine-burgundy"}"
                        >
                          ${item}
                        </button>
                      `;
                  }).join("")}
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createRecipeTypesStep(): string {
    const recipes = ["Pasta Dishes", "Grilled Meats", "Fresh Salads", "Stews & Braises", "Roasted Vegetables", "Seafood Dishes", "Cheese Boards", "Desserts"];

    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                What types of recipes do you enjoy?
              </h2>
              <p class="text-muted-foreground">
                Select the types of dishes you cook or order most often.
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${recipeBook}" alt="Recipe Book" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6 card-hover transition-all duration-300">
                <div class="flex flex-wrap justify-center gap-2">
                  ${recipes.map((item) => {
                    const isSelected = (this.userProfile.taste.recipes as string[]).includes(item);
                    return `
                        <button
                          data-category="recipes"
                          data-item="${item}"
                          class="taste-selection-btn px-3 py-1 text-lg rounded-md transition-all duration-200 ${isSelected ? "btn-primary text-primary-foreground bg-burgundy text-white shadow-wine" : "border border-input bg-background hover:border-wine-burgundy hover:text-wine-burgundy"}"
                        >
                          ${item}
                        </button>
                      `;
                  }).join("")}
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createWineTastePreferencesStep(): string {
    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                Tell us about your wine taste preferences
              </h2>
              <p class="text-muted-foreground">
                Help us understand your wine taste preferences to better match you with wines.
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${wineGrapes}" alt="Wine Occasions" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="space-y-4">
                    <div>
                      <label class="text-sm font-medium">Sweetness Preference (1-10)</label>
                      <div class="flex items-center justify-center gap-2 mt-2">
                        <span class="text-xs">Dry</span>
                        <input type="range" min="1" max="10" value="${this.userProfile.taste.sweetness_preferred_level}" class="taste-slider flex-1" data-field="sweetness_preferred_level">
                        <span class="text-xs">Sweet</span>
                        <span class="text-xs font-medium w-8">${this.userProfile.taste.sweetness_preferred_level}</span>
                      </div>
                    </div>

                    <div>
                      <label class="text-sm font-medium">Acidity Preference (1-10)</label>
                      <div class="flex items-center justify-center gap-2 mt-2">
                        <span class="text-xs">Low</span>
                        <input type="range" min="1" max="10" value="${this.userProfile.taste.acidity_preferred_level}" class="taste-slider flex-1" data-field="acidity_preferred_level">
                        <span class="text-xs">High</span>
                        <span class="text-xs font-medium w-8">${this.userProfile.taste.acidity_preferred_level}</span>
                      </div>
                    </div>

                    <div>
                      <label class="text-sm font-medium">Tannin Preference (1-10)</label>
                      <div class="flex items-center justify-center gap-2 mt-2">
                        <span class="text-xs">Light</span>
                        <input type="range" min="1" max="10" value="${this.userProfile.taste.tannin_preferred_level}" class="taste-slider flex-1" data-field="tannin_preferred_level">
                        <span class="text-xs">Bold</span>
                        <span class="text-xs font-medium w-8">${this.userProfile.taste.tannin_preferred_level}</span>
                      </div>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <div>
                      <label class="text-sm font-medium">Body Preference</label>
                      <select class="w-full p-2 border rounded mt-2 body-select">
                        <option value="light" ${this.userProfile.taste.body_preference === 'light' ? 'selected' : ''}>Light</option>
                        <option value="medium" ${this.userProfile.taste.body_preference === 'medium' ? 'selected' : ''}>Medium</option>
                        <option value="full" ${this.userProfile.taste.body_preference === 'full' ? 'selected' : ''}>Full</option>
                        <option value="no_preference" ${this.userProfile.taste.body_preference === 'no_preference' ? 'selected' : ''}>No Preference</option>
                      </select>
                    </div>

                    <div>
                      <label class="text-sm font-medium">Alcohol Sensitivity</label>
                      <select class="w-full p-2 border rounded mt-2 alcohol-sensitivity-select">
                        <option value="low" ${this.userProfile.taste.alcohol_sensitivity === 'low' ? 'selected' : ''}>Low</option>
                        <option value="medium" ${this.userProfile.taste.alcohol_sensitivity === 'medium' ? 'selected' : ''}>Medium</option>
                        <option value="high" ${this.userProfile.taste.alcohol_sensitivity === 'high' ? 'selected' : ''}>High</option>
                      </select>
                    </div>

                    <div>
                      <label class="text-sm font-medium">Preferred Alcohol Range</label>
                      <div class="flex gap-2 mt-2">
                        <input type="number" min="8" max="16" step="0.1" value="${this.userProfile.taste.alcohol_preferred_min}" class="alcohol-input flex-1 p-2 border rounded" data-field="alcohol_preferred_min" placeholder="Min %">
                        <input type="number" min="8" max="16" step="0.1" value="${this.userProfile.taste.alcohol_preferred_max}" class="alcohol-input flex-1 p-2 border rounded" data-field="alcohol_preferred_max" placeholder="Max %">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createBudgetRangeStep(): string {
    const budgetRanges = [
      { value: "budget", label: "Budget Friendly", description: "£5 - £10 per bottle", icon: "💰" },
      { value: "mid-range", label: "Mid-Range", description: "£10 - £50 per bottle", icon: "💳" },
      { value: "premium", label: "Premium", description: "£50 - £100 per bottle", icon: "💎" },
      { value: "luxury", label: "Luxury", description: "£100+ per bottle", icon: "🏆" },
    ];

    const budgetHtml = budgetRanges
      .map(
        (range) => `
          <button
            data-budget="${range.value}"
            class="budget-btn h-auto p-4 flex flex-col items-center gap-2 border rounded-md transition-all duration-200 ${this.userProfile.wine.budget === range.value ? "bg-burgundy text-white shadow-wine" : "border-input bg-background hover:border-wine-burgundy hover:shadow-gold"}"
          >
            <span class="text-2xl">${range.icon}</span>
            <span class="font-semibold">${range.label}</span>
            <span class="text-md text-center opacity-80">${range.description}</span>
          </button>
        `
      )
      .join("");

    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                What's your budget range?
              </h2>
              <p class="text-muted-foreground">
                What's your typical spending range for a bottle of wine?
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${budget}" alt="Budget" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  ${budgetHtml}
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createWineExperienceStep(): string {
    const experienceLevels = [
      { value: "beginner", label: "Wine Beginner", description: "New to wine, prefer approachable flavors", details: "Sweet wines, light reds, crisp whites" },
      { value: "intermediate", label: "Wine Enthusiast", description: "Some wine knowledge, open to exploration", details: "Varied styles, willing to try new regions" },
      { value: "advanced", label: "Wine Connoisseur", description: "Experienced, appreciate complexity", details: "Complex wines, specific varietals, terroir" },
      { value: "expert", label: "Wine Expert", description: "Deep knowledge, seek unique expressions", details: "Rare wines, vintages, specific producers" },
    ];

    const experienceHtml = experienceLevels
      .map(
        (level) => `
          <button
            data-experience="${level.value}"
            class="experience-btn h-auto p-4 flex flex-col items-start gap-2 border rounded-md transition-all duration-200 text-left ${this.userProfile.wine.experience === level.value ? "bg-burgundy text-white shadow-wine" : "border-input bg-background hover:border-wine-burgundy hover:shadow-gold"}"
          >
            <span class="font-semibold text-lg">${level.label}</span>
            <span class="text-md opacity-80">${level.description}</span>
            <span class="text-sm opacity-60">${level.details}</span>
          </button>
        `
      )
      .join("");

    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                What's your wine experience level?
              </h2>
              <p class="text-muted-foreground">
                How would you describe your wine knowledge and experience?
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${wineExperience}" alt="Wine Experience" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  ${experienceHtml}
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createWineOccasionsStep(): string {
    const occasions = ["Casual Dinner at Home", "Special Celebrations", "Date Nights", "Social Gatherings", "Business Dinners", "Weekend Relaxation", "Holiday Meals", "Outdoor Events", "Wine Tastings", "Cooking & Pairing", "Gift Giving", "Collecting"];

    const occasionsHtml = occasions
      .map((occasion) => {
        const isSelected = this.userProfile.wine.occasions.includes(occasion);
        return `
            <button
              data-occasion="${occasion}"
              class="occasion-btn h-auto p-3 text-md border rounded-md transition-all duration-200 ${isSelected ? "btn-primary bg-burgundy text-white text-primary-foreground shadow-wine" : "border-input bg-background hover:border-wine-burgundy hover:text-wine-burgundy"}"
            >
              ${occasion}
            </button>
          `;
      })
      .join("");

    return `
          <div class="animate-fade-in">
            <div class="text-center mb-8">
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                When do you enjoy wine?
              </h2>
              <p class="text-muted-foreground">
                When do you typically enjoy wine? Select all that apply.
              </p>
            </div>

            <div class="text-center mb-8">
              <img src="${party}" alt="Wine Occasions" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
            </div>

            <div class="mx-auto">
              <div class="bg-card rounded-lg p-6">
                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  ${occasionsHtml}
                </div>
              </div>
            </div>
          </div>
        `;
  }

  private createProcessingStep(): string {
    // Only start submission process if not already submitting
    if (!this.isSubmittingToAPI) {
      this.isSubmittingToAPI = true;
      this.submitProfileToAPI();
    }

    if (this.submissionError) {
      return `
        <div class="animate-fade-in">
          <div class="text-center">
            <div class="mb-8">
              <div class="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                ${Icons.alertCircle("h-8 w-8 text-red-500")}
              </div>
              <h2 class="text-2xl font-bold text-wine-dark mb-2">
                Profile Submission Failed
              </h2>
              <p class="text-muted-foreground mb-4">
                ${this.submissionError}
              </p>
              <button 
                id="retry-submission-btn"
                class="px-6 py-2 bg-wine-burgundy text-white rounded-md hover:bg-wine-dark transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      `;
    }

    return `
      <div class="animate-fade-in">
        <div class="text-center">
          <div class="mb-8">
            <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-burgundy mx-auto mb-4"></div>
            <h2 class="text-2xl font-bold text-wine-dark mb-2">
              Creating your wine profile
            </h2>
            <p class="text-muted-foreground">
              Please wait while we analyze your preferences and save your personalized wine profile...
            </p>
          </div>
        </div>
      </div>
    `;
  }

  private async submitProfileToAPI(): Promise<void> {
    try {
      // Submit the profile to the API (backend will determine wine profile type)
      const profileTypeData = await submitUserProfile(this.userProfile);
      
      if (profileTypeData) {
        // Store the determined profile type from backend
        this.determinedProfileType = profileTypeData;
        console.log("Received profile type from backend:", profileTypeData);
        
        // Profile submitted successfully, show the profile type page
        setTimeout(() => {
          this.isProcessing = false;
          this.showProfileType = true;
          this.render();
        }, 2000); // Shorter delay since we actually did work
      } else {
        // Show error state
        this.submissionError = "Failed to save your wine profile. Please check your connection and try again.";
        this.render();
      }
    } catch (error) {
      console.error("Profile submission error:", error);
      this.submissionError = "An unexpected error occurred while saving your profile. Please try again.";
      this.render();
    }
  }

  private getWineProfileType() {
    const experience = this.userProfile.wine.experience;
    const budget = this.userProfile.wine.budget;

    // Simple logic to determine profile type
    if (experience === 'beginner' && budget === 'budget') {
      return {
        type: "The Curious Explorer",
        description: "You're just starting your wine journey and love discovering new flavors at accessible prices.",
        characteristics: ["Budget-conscious", "Open to learning", "Enjoys variety", "Seeks approachable wines"],
        recommendations: ["Start with lighter wines", "Try wine and food pairings", "Explore different regions gradually"]
      };
    } else if (experience === 'intermediate' && (budget === 'mid-range' || budget === 'premium')) {
      return {
        type: "The Enthusiastic Connoisseur",
        description: "You have developing wine knowledge and are ready to explore more complex flavors and styles.",
        characteristics: ["Growing wine knowledge", "Willing to invest", "Enjoys food pairing", "Seeks quality"],
        recommendations: ["Explore single-vineyard wines", "Try vertical tastings", "Learn about wine regions"]
      };
    } else if (experience === 'advanced' || experience === 'expert') {
      return {
        type: "The Wine Aficionado",
        description: "You have extensive wine knowledge and appreciate the nuances of exceptional bottles.",
        characteristics: ["Deep wine knowledge", "Seeks rare finds", "Values terroir", "Collects wines"],
        recommendations: ["Invest in aged wines", "Explore emerging regions", "Try natural and biodynamic wines"]
      };
    } else {
      return {
        type: "The Balanced Enthusiast",
        description: "You enjoy wine as part of a well-rounded lifestyle, balancing quality with value.",
        characteristics: ["Practical approach", "Values consistency", "Enjoys social wine experiences", "Seeks versatile wines"],
        recommendations: ["Focus on food-friendly wines", "Build a versatile cellar", "Join wine clubs or tastings"]
      };
    }
  }

  private createWineProfileTypeStep(): string {
    // Use the backend-determined profile type if available, otherwise fallback to local determination
    const profileType = this.determinedProfileType || this.getWineProfileType();

    return `
      <div class="animate-fade-in">
        <div class="text-center mb-8">
          <div class="flex items-center justify-center gap-2 mb-4">
            ${Icons.checkCircle("h-8 w-8 text-green-600")}
            <h2 class="text-2xl font-bold text-wine-dark">
              Your Wine Profile Type
            </h2>
          </div>
          <p class="text-muted-foreground">
            Based on your preferences, we've identified your unique wine profile.
          </p>
        </div>

        <div class="max-w-4xl mx-auto">
          <div class="bg-gradient-to-br from-wine-cream to-wine-light border border-wine-burgundy rounded-lg p-8 mb-8">
            <div class="text-center mb-6">
              <h3 class="text-3xl font-bold text-wine-dark mb-3">${profileType.type}</h3>
              <p class="text-lg text-wine-dark opacity-90">${profileType.description}</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div class="bg-white rounded-lg p-6">
                <h4 class="font-bold text-wine-dark mb-4 flex items-center gap-2">
                  ${Icons.sparkles("h-5 w-5 text-wine-burgundy")}
                  Your Characteristics
                </h4>
                <ul class="space-y-2">
                  ${profileType.characteristics.map((char: string) => `
                    <li class="flex items-center gap-2 text-sm">
                      ${Icons.checkCircle("h-4 w-4 text-green-600")}
                      ${char}
                    </li>
                  `).join('')}
                </ul>
              </div>

              <div class="bg-white rounded-lg p-6">
                <h4 class="font-bold text-wine-dark mb-4 flex items-center gap-2">
                  ${Icons.wine("h-5 w-5 text-wine-burgundy")}
                  Recommendations
                </h4>
                <ul class="space-y-2">
                  ${profileType.recommendations.map((rec: string) => `
                    <li class="flex items-center gap-2 text-sm">
                      ${Icons.checkCircle("h-4 w-4 text-green-600")}
                      ${rec}
                    </li>
                  `).join('')}
                </ul>
              </div>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              id="recommendations-btn"
              class="btn-primary text-primary-foreground px-6 py-3 rounded-md transition-all duration-300 flex items-center gap-2"
            >
              ${Icons.wine("h-5 w-5")}
              Get Wine Recommendations
            </button>

            <button
              id="download-btn"
              class="border border-input bg-background hover:border-wine-burgundy hover:text-wine-burgundy px-6 py-3 rounded-md transition-colors flex items-center gap-2"
            >
              ${Icons.download("h-4 w-4")}
              Download Profile
            </button>

            <button
              id="share-btn"
              class="border border-input bg-background hover:border-wine-burgundy hover:text-wine-burgundy px-6 py-3 rounded-md transition-colors flex items-center gap-2"
            >
              ${Icons.share("h-4 w-4")}
              Share Profile
            </button>
          </div>

          <div class="mt-8 text-center">
            <p class="text-sm text-muted-foreground">
              Your personalized wine profile is ready to help you discover amazing wines.
            </p>
          </div>
        </div>
      </div>
    `;
  }


  private createWineTypesStep(): string {
    const wineTypes = [
      { color: "red", label: "Red Wine", description: "Full-bodied reds like Cabernet, Merlot" },
      { color: "white", label: "White Wine", description: "Crisp whites like Chardonnay, Sauvignon Blanc" },
      { color: "rose", label: "Rosé Wine", description: "Light, refreshing pink wines" },
      { color: "sparkling", label: "Sparkling Wine", description: "Champagne, Prosecco, Cava" },
      { color: "dessert", label: "Dessert Wine", description: "Sweet wines like Port, Moscato" },
      { color: "fortified", label: "Fortified Wine", description: "Sherry, Madeira, Vermouth" },
    ];

    const createRatingSlider = (wineType: any) => {
      const currentRating = this.userProfile.wine.wine_types?.find(wt => wt.wine_color === wineType.color)?.preference_level || 5;
      return `
        <div class="bg-card rounded-lg p-6">
          <div class="mb-4">
            <h3 class="font-semibold text-lg">${wineType.label}</h3>
            <p class="text-sm text-muted-foreground">${wineType.description}</p>
          </div>
          <div class="space-y-3">
            <div class="flex justify-between text-sm">
              <span>Not interested</span>
              <span class="font-semibold">Rating: ${currentRating}/10</span>
              <span>Love it</span>
            </div>
            <input 
              type="range" 
              min="1" 
              max="10" 
              value="${currentRating}" 
              class="wine-type-slider w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              data-wine-color="${wineType.color}"
            >
            <div class="flex justify-between text-xs text-muted-foreground">
              <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span><span>7</span><span>8</span><span>9</span><span>10</span>
            </div>
          </div>
        </div>
      `;
    };

    return `
      <div class="animate-fade-in">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-wine-dark mb-2">Rate your wine type preferences</h2>
          <p class="text-muted-foreground">
            Rate each wine type from 1-10 based on how much you enjoy them.
          </p>
        </div>

        <div class="text-center mb-8">
          <img src="${wineColours}" alt="Wine Types" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          ${wineTypes.map(type => createRatingSlider(type)).join('')}
        </div>
      </div>
    `;
  }

  private createWineRegionsStep(): string {
    const regions = [
      { region_name: "Bordeaux", country: "France" },
      { region_name: "Burgundy", country: "France" },
      { region_name: "Champagne", country: "France" },
      { region_name: "Loire Valley", country: "France" },
      { region_name: "Tuscany", country: "Italy" },
      { region_name: "Piedmont", country: "Italy" },
      { region_name: "Rioja", country: "Spain" },
      { region_name: "Napa Valley", country: "United States" },
      { region_name: "Sonoma County", country: "United States" },
      { region_name: "Barossa Valley", country: "Australia" },
      { region_name: "Marlborough", country: "New Zealand" },
      { region_name: "Mendoza", country: "Argentina" }
    ];

    const createRegionCard = (region: any) => {
      const isSelected = this.userProfile.wine.preferred_regions?.some(r => r.region_name === region.region_name);
      const preference = this.userProfile.wine.preferred_regions?.find(r => r.region_name === region.region_name);
      const rating = preference?.preference_level || 5;

      return `
        <div class="bg-card rounded-lg p-4 ${isSelected ? 'border-wine-burgundy' : ''}">
          <div class="flex justify-between items-center mb-3">
            <div>
              <h3 class="font-semibold">${region.region_name}</h3>
              <p class="text-sm text-muted-foreground">${region.country}</p>
            </div>
            <button 
              class="region-toggle-btn px-3 py-1 text-sm rounded ${isSelected ? 'bg-burgundy text-white' : 'border border-input'}"
              data-region="${region.region_name}"
              data-country="${region.country}"
            >
              ${isSelected ? 'Selected' : 'Select'}
            </button>
          </div>
          ${isSelected ? `
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span>Preference: ${rating}/10</span>
              </div>
              <input 
                type="range" 
                min="1" 
                max="10" 
                value="${rating}" 
                class="region-rating-slider w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                data-region="${region.region_name}"
              >
            </div>
          ` : ''}
        </div>
      `;
    };

    return `
      <div class="animate-fade-in">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-wine-dark mb-2">Select your favorite wine regions</h2>
          <p class="text-muted-foreground">
            Choose regions you enjoy and rate your preference level.
          </p>
        </div>

        <div class="text-center mb-8">
          <img src="${globe}" alt="Wine Occasions" class="mx-auto h-32 xs:h-40 sm:h-56 md:h-80 lg:h-96 w-auto max-w-full object-cover" />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          ${regions.map(region => createRegionCard(region)).join('')}
        </div>
      </div>
    `;
  }

  private createAdvancedFlavorProfilesStep(): string {
    const flavorCategories = [
      { category: "fruit", descriptors: ["Cherry", "Blackberry", "Apple", "Citrus", "Tropical", "Stone Fruit"] },
      { category: "spice", descriptors: ["Black Pepper", "Cinnamon", "Clove", "Nutmeg", "Ginger", "Vanilla"] },
      { category: "herb", descriptors: ["Rosemary", "Thyme", "Sage", "Mint", "Eucalyptus", "Grass"] },
      { category: "earth", descriptors: ["Mushroom", "Soil", "Wet Leaves", "Forest Floor", "Tar", "Leather"] },
      { category: "mineral", descriptors: ["Slate", "Limestone", "Chalk", "Flint", "Wet Stone", "Saline"] },
      { category: "oak", descriptors: ["Vanilla", "Toast", "Smoke", "Cedar", "Coffee", "Chocolate"] }
    ];

    const createFlavorSection = (title: string, type: 'loved' | 'disliked') => {
      const flavors = type === 'loved' ? this.userProfile.wine.loved_flavors || [] : this.userProfile.wine.disliked_flavors || [];

      return `
        <div class="bg-card rounded-lg p-6">
          <h3 class="text-lg font-semibold mb-4">${title}</h3>
          ${flavorCategories.map(cat => `
            <div class="mb-4">
              <h4 class="font-medium capitalize mb-2">${cat.category}</h4>
              <div class="flex flex-wrap justify-center gap-2">
                ${cat.descriptors.map(desc => {
                  const existing = flavors.find(f => f.category === cat.category && f.descriptor === desc);
                  const isSelected = !!existing;
                  return `
                    <button 
                      class="flavor-btn px-3 py-1 text-sm rounded transition-colors ${isSelected ? 'bg-burgundy text-white' : 'border border-input hover:border-wine-burgundy'}"
                      data-type="${type}"
                      data-category="${cat.category}"
                      data-descriptor="${desc}"
                      data-intensity="${existing?.intensity || 5}"
                    >
                      ${desc} ${isSelected ? `(${existing?.intensity}/10)` : ''}
                    </button>
                  `;
                }).join('')}
              </div>
            </div>
          `).join('')}
        </div>
      `;
    };

    return `
      <div class="animate-fade-in">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-wine-dark mb-2">Tell us about flavor preferences</h2>
          <p class="text-muted-foreground">
            Select flavors you love or dislike in wine. Click to toggle and adjust intensity.
          </p>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          ${createFlavorSection("Flavors You Love", "loved")}
          ${createFlavorSection("Flavors You Dislike", "disliked")}
        </div>
      </div>
    `;
  }

  private createDrinkingHabitsStep(): string {
    const frequencies = [
      { value: "rarely", label: "Rarely", description: "Special occasions only" },
      { value: "monthly", label: "Monthly", description: "A few times per month" },
      { value: "weekly", label: "Weekly", description: "Once or twice a week" },
      { value: "several_times_week", label: "Several times a week", description: "3-4 times per week" },
      { value: "daily", label: "Daily", description: "Most days of the week" },
    ];

    const habits = this.userProfile.wine.drinking_habits!;

    return `
      <div class="animate-fade-in">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-wine-dark mb-2">Tell us about your drinking habits</h2>
          <p class="text-muted-foreground">
            This helps us understand your wine consumption patterns and budget preferences.
          </p>
        </div>

        <div class="space-y-6">
          <div class="bg-card rounded-lg p-6">
            <h3 class="text-lg font-semibold mb-4">How often do you drink wine?</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              ${frequencies.map(freq => `
                <button
                  type="button"
                  class="frequency-btn p-3 text-left border rounded transition-colors cursor-pointer ${habits.frequency === freq.value ? 'bg-burgundy text-white' : 'border-input hover:border-wine-burgundy'}"
                  data-frequency="${freq.value}"
                  style="user-select: none;"
                >
                  <div class="font-medium pointer-events-none">${freq.label}</div>
                  <div class="text-sm opacity-80 pointer-events-none">${freq.description}</div>
                </button>
              `).join('')}
            </div>
          </div>

          <div class="bg-card rounded-lg p-6">
            <h3 class="text-lg font-semibold mb-4">Budget Ranges</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 class="font-medium mb-3">Everyday Wine Budget</h4>
                <div class="space-y-3">
                  <div>
                    <label class="text-sm">Minimum per bottle ($)</label>
                    <input
                      type="number"
                      class="budget-input w-full p-2 border rounded"
                      data-field="everyday_wine_min_budget"
                      value="${habits.everyday_wine_min_budget || ''}"
                      placeholder="15"
                    >
                  </div>
                  <div>
                    <label class="text-sm">Maximum per bottle ($)</label>
                    <input
                      type="number"
                      class="budget-input w-full p-2 border rounded"
                      data-field="everyday_wine_max_budget"
                      value="${habits.everyday_wine_max_budget || ''}"
                      placeholder="35"
                    >
                  </div>
                </div>
              </div>
              <div>
                <h4 class="font-medium mb-3">Special Occasion Wine Budget</h4>
                <div class="space-y-3">
                  <div>
                    <label class="text-sm">Minimum per bottle ($)</label>
                    <input
                      type="number"
                      class="budget-input w-full p-2 border rounded"
                      data-field="special_occasion_min_budget"
                      value="${habits.special_occasion_min_budget || ''}"
                      placeholder="50"
                    >
                  </div>
                  <div>
                    <label class="text-sm">Maximum per bottle ($)</label>
                    <input 
                      type="number"
                      class="budget-input w-full p-2 border rounded"
                      data-field="special_occasion_max_budget"
                      value="${habits.special_occasion_max_budget || ''}"
                      placeholder="150"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private createFinalDetailsStep(): string {
    const dietaryRestrictions = [
      "vegetarian", "vegan", "gluten_free", "dairy_free", "low_sodium", "keto", "none"
    ];

    const pairingStyles = [
      "complement", "contrast", "regional_matching", "seasonal", "no_preference"
    ];

    return `
      <div class="animate-fade-in">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-wine-dark mb-2">Final details</h2>
          <p class="text-muted-foreground">
            Last few details to complete your wine profile.
          </p>
        </div>

        <div class="space-y-6">
          <div class="bg-card rounded-lg p-6">
            <h3 class="text-lg font-semibold mb-4">Dietary Restrictions</h3>
            <div class="flex flex-wrap justify-center gap-2">
              ${dietaryRestrictions.map(restriction => {
                const isSelected = this.userProfile.wine.dietary_restrictions?.includes(restriction);
                return `
                  <button 
                    class="dietary-btn px-3 py-2 text-sm rounded transition-colors ${isSelected ? 'bg-burgundy text-white' : 'border border-input hover:border-wine-burgundy'}"
                    data-restriction="${restriction}"
                  >
                    ${restriction.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                `;
              }).join('')}
            </div>
          </div>

          <div class="bg-card rounded-lg p-6">
            <h3 class="text-lg font-semibold mb-4">Preferred Pairing Styles</h3>
            <div class="flex flex-wrap justify-center gap-2">
              ${pairingStyles.map(style => {
                const isSelected = this.userProfile.wine.pairing_styles?.includes(style);
                return `
                  <button 
                    class="pairing-btn px-3 py-2 text-sm rounded transition-colors ${isSelected ? 'bg-burgundy text-white' : 'border border-input hover:border-wine-burgundy'}"
                    data-style="${style}"
                  >
                    ${style.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                `;
              }).join('')}
            </div>
          </div>

          <div class="bg-card rounded-lg p-6">
            <h3 class="text-lg font-semibold mb-4">Openness to New Wines</h3>
            <p class="text-sm text-muted-foreground mb-4">How open are you to trying new and unusual wines?</p>
            <div class="space-y-3">
              <div class="flex justify-between text-sm">
                <span>Prefer familiar wines</span>
                <span class="font-semibold">Rating: ${this.userProfile.wine.openness_to_new_wines}/10</span>
                <span>Love discovering new wines</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value="${this.userProfile.wine.openness_to_new_wines}" 
                class="openness-slider w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              >
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private render(): void {
    if (this.showWelcome) {
      getElementById("section-progress").innerHTML = "";
      getElementById("section-stepContent").innerHTML = this.createWelcomeStep();
      getElementById("section-navigation").innerHTML = "";
      this.attachEventListeners();
    } else if (this.isProcessing) {
      getElementById("section-progress").innerHTML = "";
      getElementById("section-stepContent").innerHTML = this.createProcessingStep();
      getElementById("section-navigation").innerHTML = "";
    } else if (this.showProfileType) {
      getElementById("section-progress").innerHTML = "";
      getElementById("section-stepContent").innerHTML = this.createWineProfileTypeStep();
      getElementById("section-navigation").innerHTML = "";
      this.attachEventListeners();
    } else {
      getElementById("section-progress").innerHTML = this.createProgress();
      getElementById("section-stepContent").innerHTML = this.createStepContent();
      getElementById("section-navigation").innerHTML = this.createNavigation();
      this.attachEventListeners();
    }
  }

  private renderWithTransition(): void {
    const stepContent = getElementById("section-stepContent");
    const progress = getElementById("section-progress");
    const navigation = getElementById("section-navigation");

    // Add fade-out class
    stepContent.classList.add("opacity-0");
    progress.classList.add("opacity-0");
    navigation.classList.add("opacity-0");

    // Wait for fade-out to complete, then update content
    setTimeout(() => {
      if (this.showWelcome) {
        progress.innerHTML = "";
        stepContent.innerHTML = this.createWelcomeStep();
        navigation.innerHTML = "";
        this.attachEventListeners();
      } else if (this.isProcessing) {
        progress.innerHTML = "";
        stepContent.innerHTML = this.createProcessingStep();
        navigation.innerHTML = "";
      } else if (this.showProfileType) {
        progress.innerHTML = "";
        stepContent.innerHTML = this.createWineProfileTypeStep();
        navigation.innerHTML = "";
        this.attachEventListeners();
      } else {
        progress.innerHTML = this.createProgress();
        stepContent.innerHTML = this.createStepContent();
        navigation.innerHTML = this.createNavigation();
        this.attachEventListeners();
      }

      // Add transition classes and fade-in
      stepContent.classList.add("transition-opacity", "duration-300");
      progress.classList.add("transition-opacity", "duration-300");
      navigation.classList.add("transition-opacity", "duration-300");
      
      // Fade back in
      requestAnimationFrame(() => {
        stepContent.classList.remove("opacity-0");
        progress.classList.remove("opacity-0");
        navigation.classList.remove("opacity-0");
      });
    }, 150);
  }

  private attachEventListeners(): void {
    // Welcome page button
    const getStartedBtn = document.getElementById("get-started-btn");
    if (getStartedBtn) getStartedBtn.addEventListener("click", () => this.nextStep());

    // Navigation buttons
    const prevBtn = document.getElementById("prev-btn");
    const nextBtn = document.getElementById("next-btn");

    if (prevBtn) prevBtn.addEventListener("click", () => this.prevStep());
    if (nextBtn) nextBtn.addEventListener("click", () => this.nextStep());

    // Step 1: Taste preferences
    const tasteButtons = document.querySelectorAll(".taste-selection-btn");
    tasteButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const button = e.target as HTMLButtonElement;
        const category = button.dataset.category as keyof TastePreferences;
        const item = button.dataset.item!;

        const currentItems = this.userProfile.taste[category] as string[];
        const newItems = currentItems.includes(item) ? currentItems.filter((i: string) => i !== item) : [...currentItems, item];

        this.updateTastePreferences({
          ...this.userProfile.taste,
          [category]: newItems,
        });
      });
    });

    // Step 1: Taste preference controls
    const tasteSliders = document.querySelectorAll(".taste-slider");
    tasteSliders.forEach((slider) => {
      slider.addEventListener("input", (e) => {
        const input = e.target as HTMLInputElement;
        const field = input.dataset.field!;
        const value = parseInt(input.value);

        this.updateTastePreferences({
          ...this.userProfile.taste,
          [field]: value
        });
      });
    });

    const bodySelect = document.querySelector(".body-select");
    if (bodySelect) {
      bodySelect.addEventListener("change", (e) => {
        const select = e.target as HTMLSelectElement;
        this.updateTastePreferences({
          ...this.userProfile.taste,
          body_preference: select.value
        });
      });
    }

    const alcoholSensitivitySelect = document.querySelector(".alcohol-sensitivity-select");
    if (alcoholSensitivitySelect) {
      alcoholSensitivitySelect.addEventListener("change", (e) => {
        const select = e.target as HTMLSelectElement;
        this.updateTastePreferences({
          ...this.userProfile.taste,
          alcohol_sensitivity: select.value
        });
      });
    }

    const alcoholInputs = document.querySelectorAll(".alcohol-input");
    alcoholInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        const inputElement = e.target as HTMLInputElement;
        const field = inputElement.dataset.field!;
        const value = parseFloat(inputElement.value);

        this.updateTastePreferences({
          ...this.userProfile.taste,
          [field]: value
        });
      });
    });

    // Step 2: Wine profile - Use event delegation (only attach once)
    if (!this.eventListenersAttached) {
      document.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;

        // Budget button clicked
        if (target.closest(".budget-btn")) {
          const button = target.closest(".budget-btn") as HTMLButtonElement;
          const budget = button.dataset.budget!;
          this.updateWineProfile({ ...this.userProfile.wine, budget });
        }

        // Experience button clicked
        if (target.closest(".experience-btn")) {
          const button = target.closest(".experience-btn") as HTMLButtonElement;
          const experience = button.dataset.experience!;
          this.updateWineProfile({ ...this.userProfile.wine, experience });
        }
      });
      this.eventListenersAttached = true;
    }

    const occasionButtons = document.querySelectorAll(".occasion-btn");
    occasionButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const button = e.target as HTMLButtonElement;
        const occasion = button.dataset.occasion!;

        const newOccasions = this.userProfile.wine.occasions.includes(occasion) ? this.userProfile.wine.occasions.filter((o) => o !== occasion) : [...this.userProfile.wine.occasions, occasion];

        this.updateWineProfile({ ...this.userProfile.wine, occasions: newOccasions });
      });
    });

    // Step 3: Profile summary actions
    const recommendationsBtn = document.getElementById("recommendations-btn");
    const downloadBtn = document.getElementById("download-btn");
    const shareBtn = document.getElementById("share-btn");

    // Retry submission button
    const retrySubmissionBtn = document.getElementById("retry-submission-btn");
    if (retrySubmissionBtn) {
      retrySubmissionBtn.addEventListener("click", () => {
        // Reset error state and retry submission
        this.submissionError = null;
        this.isSubmittingToAPI = false;
        this.render();
      });
    }

    if (recommendationsBtn) {
      recommendationsBtn.addEventListener("click", () => {
        console.log("Generating wine recommendations based on profile:", this.userProfile);
        alert("Wine recommendations would be generated here!");
      });
    }

    if (downloadBtn) {
      downloadBtn.addEventListener("click", () => {
        const profileData = JSON.stringify(this.userProfile, null, 2);
        const blob = new Blob([profileData], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "wine-profile.json";
        a.click();
        URL.revokeObjectURL(url);
      });
    }

    if (shareBtn) {
      shareBtn.addEventListener("click", () => {
        if (navigator.share) {
          navigator.share({
            title: "My Wine Profile",
            text: "Check out my personalized wine profile!",
            url: window.location.href,
          });
        } else {
          alert("Sharing would be implemented here!");
        }
      });
    }

    // Step 3: Wine type sliders
    const wineTypeSliders = document.querySelectorAll(".wine-type-slider");
    wineTypeSliders.forEach((slider) => {
      slider.addEventListener("input", (e) => {
        const input = e.target as HTMLInputElement;
        const wineColor = input.dataset.wineColor!;
        const rating = parseInt(input.value);

        const currentTypes = this.userProfile.wine.wine_types || [];
        const newTypes = currentTypes.filter(wt => wt.wine_color !== wineColor);
        newTypes.push({ wine_color: wineColor, preference_level: rating });

        this.updateWineProfile({ ...this.userProfile.wine, wine_types: newTypes });
      });
    });

    // Step 4: Wine regions
    const regionToggleBtns = document.querySelectorAll(".region-toggle-btn");
    regionToggleBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const button = e.target as HTMLButtonElement;
        const regionName = button.dataset.region!;
        const country = button.dataset.country!;

        const currentRegions = this.userProfile.wine.preferred_regions || [];
        const exists = currentRegions.some(r => r.region_name === regionName);

        let newRegions;
        if (exists) {
          newRegions = currentRegions.filter(r => r.region_name !== regionName);
        } else {
          newRegions = [...currentRegions, { region_name: regionName, country, preference_level: 5 }];
        }

        this.updateWineProfile({ ...this.userProfile.wine, preferred_regions: newRegions });
      });
    });

    const regionRatingSliders = document.querySelectorAll(".region-rating-slider");
    regionRatingSliders.forEach((slider) => {
      slider.addEventListener("input", (e) => {
        const input = e.target as HTMLInputElement;
        const regionName = input.dataset.region!;
        const rating = parseInt(input.value);

        const currentRegions = this.userProfile.wine.preferred_regions || [];
        const newRegions = currentRegions.map(r => 
          r.region_name === regionName ? { ...r, preference_level: rating } : r
        );

        this.updateWineProfile({ ...this.userProfile.wine, preferred_regions: newRegions });
      });
    });

    // Step 5: Flavor profiles
    const flavorBtns = document.querySelectorAll(".flavor-btn");
    flavorBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const button = e.target as HTMLButtonElement;
        const type = button.dataset.type as 'loved' | 'disliked';
        const category = button.dataset.category!;
        const descriptor = button.dataset.descriptor!;

        const currentFlavors = type === 'loved' ? this.userProfile.wine.loved_flavors || [] : this.userProfile.wine.disliked_flavors || [];
        const exists = currentFlavors.some(f => f.category === category && f.descriptor === descriptor);

        let newFlavors;
        if (exists) {
          newFlavors = currentFlavors.filter(f => !(f.category === category && f.descriptor === descriptor));
        } else {
          const intensity = parseInt(button.dataset.intensity!) || 5;
          newFlavors = [...currentFlavors, { category, descriptor, intensity }];
        }

        if (type === 'loved') {
          this.updateWineProfile({ ...this.userProfile.wine, loved_flavors: newFlavors });
        } else {
          this.updateWineProfile({ ...this.userProfile.wine, disliked_flavors: newFlavors });
        }
      });
    });

    // Step 6: Drinking habits
    const frequencyBtns = document.querySelectorAll(".frequency-btn");
    frequencyBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const button = e.currentTarget as HTMLButtonElement;
        const frequency = button.dataset.frequency!;

        this.updateWineProfile({
          ...this.userProfile.wine,
          drinking_habits: { ...this.userProfile.wine.drinking_habits!, frequency }
        });
      });
    });

    const budgetInputs = document.querySelectorAll(".budget-input");
    budgetInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        const inputElement = e.target as HTMLInputElement;
        const field = inputElement.dataset.field!;
        const value = parseFloat(inputElement.value) || 0;

        this.updateWineProfile({
          ...this.userProfile.wine,
          drinking_habits: { ...this.userProfile.wine.drinking_habits!, [field]: value }
        });
      });
    });

    // Step 7: Final details
    const dietaryBtns = document.querySelectorAll(".dietary-btn");
    dietaryBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const button = e.target as HTMLButtonElement;
        const restriction = button.dataset.restriction!;

        const currentRestrictions = this.userProfile.wine.dietary_restrictions || [];
        const newRestrictions = currentRestrictions.includes(restriction)
          ? currentRestrictions.filter(r => r !== restriction)
          : [...currentRestrictions, restriction];

        this.updateWineProfile({ ...this.userProfile.wine, dietary_restrictions: newRestrictions });
      });
    });

    const pairingBtns = document.querySelectorAll(".pairing-btn");
    pairingBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const button = e.target as HTMLButtonElement;
        const style = button.dataset.style!;

        const currentStyles = this.userProfile.wine.pairing_styles || [];
        const newStyles = currentStyles.includes(style)
          ? currentStyles.filter(s => s !== style)
          : [...currentStyles, style];

        this.updateWineProfile({ ...this.userProfile.wine, pairing_styles: newStyles });
      });
    });

    const opennessSlider = document.querySelector(".openness-slider");
    if (opennessSlider) {
      opennessSlider.addEventListener("input", (e) => {
        const input = e.target as HTMLInputElement;
        const openness = parseInt(input.value);

        this.updateWineProfile({ ...this.userProfile.wine, openness_to_new_wines: openness });
      });
    }
  }
}
